/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../state/reducers";
import useClinic from "../hooks/useClinic";
import useOAuthToken from "../hooks/useOAuthToken";
import SehatSpinner from "../components/SehatSpinner";
import { LoginView } from "../routes/login/LoginRoute";
import SelectClinicRoute from "../routes/SelectClinic/SelectClinicRoute";

function useEnsureAuthenticated() {
  const { hasToken } = useOAuthToken();
  const { loggingOut } = useSelector((state: RootState) => state.auth);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);

  const authReady = hasToken && !loggingOut && currentUser;
  return { authReady };
}

export function SecuredRoute({ children }: { children: React.ReactNode }) {
  const { authReady } = useEnsureAuthenticated();

  // don't navigate to /login, just show the route. Then we'll know where we are
  // after we log in.
  if (!authReady) {
    return <LoginView />;
  }

  return <>{children}</>;
}

export function SecuredClinicRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const { authReady } = useEnsureAuthenticated();
  const { clinic, isFetchingClinic } = useClinic();

  // don't navigate to /login, just show the route. Then we'll know where we are
  // after we log in.
  if (!authReady) {
    return <LoginView />;
  }
  if (isFetchingClinic) {
    return <SehatSpinner />;
  }

  // similar for /select-clinic as for /login.
  if (!clinic) {
    return <SelectClinicRoute />;
  }

  return <>{children}</>;
}
